import React, { useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import config from '../config/config';
import { authFetch } from '../utils/api';
import './RuleEditor.css';

// ── Templates ──────────────────────────────────────────────────────────────────

const TEMPLATES = {
  sqlInjection: `# Vigil Rule — SQL Injection Detection

rule my-sql-injection:
  version: "1.0.0"
  severity: high
  confidence: medium
  languages: [go, java, python, javascript]
  cwe: ["CWE-89"]
  owasp: ["A03:2021-Injection"]

  query: |
    find flow
      from @user_input
      to @sql.execute
      where not uses(@parameterized_query)
      within @http_handler

  exclude:
    paths: ["**/tests/**", "**/vendor/**"]

  report:
    message: "User input flows to SQL execution without parameterization."
    fix: "Use parameterized queries or an ORM."
`,
  hardcodedSecrets: `# Vigil Rule — Hardcoded Secrets

rule my-hardcoded-secrets:
  version: "1.0.0"
  severity: critical
  confidence: high
  languages: [go, java, python, javascript]
  cwe: ["CWE-798"]
  owasp: ["A02:2021-Cryptographic Failures"]

  query: |
    find usage
      of @credentials.hardcoded

  exclude:
    paths: ["**/tests/**", "**/*_test.go"]

  report:
    message: "Hardcoded credential detected in source code."
    fix: "Use environment variables or a secrets manager."
`,
  weakCrypto: `# Vigil Rule — Weak Cryptographic Algorithm

rule my-weak-crypto:
  version: "1.0.0"
  severity: high
  confidence: high
  languages: [go, java, python, javascript]
  cwe: ["CWE-327"]
  owasp: ["A02:2021-Cryptographic Failures"]

  query: |
    find call
      to any(@crypto.md5, @crypto.sha1)

  report:
    message: "Weak cryptographic algorithm detected (MD5/SHA-1)."
    fix: "Use SHA-256 or stronger. For passwords, use bcrypt or argon2."
`,
  empty: `# Vigil Rule — Custom Rule

rule my-custom-rule:
  version: "1.0.0"
  severity: medium
  confidence: medium
  languages: [go]
  cwe: ["CWE-000"]

  query: |
    find flow
      from @user_input
      to @sql.execute

  report:
    message: "Describe the security issue here."
    fix: "Describe the remediation here."
`,
  emptyFunction: `# Vigil Rule — Empty Function Body
# Uses "find pattern" to match structural code patterns directly

rule empty-function-body:
  version: "1.0.0"
  severity: info
  confidence: high
  languages: [go]

  query: |
    find pattern "func $NAME(...) { }"

  report:
    message: "Empty function body — may indicate unfinished implementation."
    fix: "Add implementation or remove the function."
`,
  todoComment: `# Vigil Rule — TODO/FIXME Comments
# Uses "find pattern" with regex to find unresolved items

rule todo-fixme-comments:
  version: "1.0.0"
  severity: info
  confidence: high
  languages: [go, java, python, javascript]

  query: |
    find pattern "// TODO: ..."

  report:
    message: "Unresolved TODO comment found."
    fix: "Address the TODO item or create a ticket to track it."
`,
  emptyTryCatch: `# Vigil Rule — Empty Catch Block (Java)
# Catches silent exception swallowing

rule empty-catch-block:
  version: "1.0.0"
  severity: medium
  confidence: high
  languages: [java]
  cwe: ["CWE-390"]

  query: |
    find pattern "catch ($TYPE $EX) { }"

  report:
    message: "Empty catch block silently swallows exceptions."
    fix: "Log the exception or handle it appropriately."
`,
  debugLogging: `# Vigil Rule — Debug Print Statements
# Finds leftover debug output in production code

rule debug-print-statements:
  version: "1.0.0"
  severity: low
  confidence: medium
  languages: [go, python, javascript]

  query: |
    find pattern "fmt.Println(...)"

  report:
    message: "Debug print statement found in production code."
    fix: "Use a proper logging framework instead of fmt.Println."
`,
  weakCryptoImport: `# Vigil Rule — Weak Crypto Import
# Uses "find import" to detect imports of weak crypto packages

rule weak-crypto-import:
  version: "1.0.0"
  severity: high
  confidence: high
  languages: [go, java, python]

  query: |
    find import @import.crypto

  report:
    message: "Import of cryptographic library detected — verify algorithm strength."
    fix: "Use strong algorithms (AES-256, SHA-256+). Avoid MD5 and SHA-1."
`,
  deprecatedAnnotation: `# Vigil Rule — Deprecated Code Usage
# Uses "find annotation" to locate deprecated APIs

rule deprecated-annotation:
  version: "1.0.0"
  severity: low
  confidence: high
  languages: [java]

  query: |
    find annotation "@Deprecated"

  report:
    message: "Deprecated API usage found."
    fix: "Replace with the recommended alternative API."
`,
  passwordVariable: `# Vigil Rule — Password Variable Declaration
# Uses "find variable" to find hardcoded password variables

rule password-variable:
  version: "1.0.0"
  severity: high
  confidence: medium
  languages: [java, python, javascript, go]

  query: |
    find variable "password"

  report:
    message: "Variable named 'password' found — may contain hardcoded credentials."
    fix: "Use environment variables or a secrets manager for credentials."
`,
  unsafeClassInheritance: `# Vigil Rule — Unsafe Deserialization Base Class
# Uses "find class" with "extends" to detect risky inheritance

rule unsafe-deserialization-class:
  version: "1.0.0"
  severity: high
  confidence: medium
  languages: [java]

  query: |
    find class
      where implements "Serializable"

  report:
    message: "Class implements Serializable — review for unsafe deserialization."
    fix: "Add readObject/writeObject guards or use safe serialization frameworks."
`,
};

// ── Concept Browser Data ───────────────────────────────────────────────────────

const CONCEPTS = {
  source:    ['@user_input', '@env_var', '@file_read', '@credentials.hardcoded'],
  sink:      ['@sql.execute', '@os.exec', '@http.outbound_request', '@file.write', '@logging.write', '@html.render', '@url.redirect', '@deserialize', '@template.render', '@http.set_header'],
  sanitizer: ['@parameterized_query', '@html.escape', '@url.encode', '@path.clean', '@input.sanitize'],
  guard:     ['@http_handler', '@auth.middleware', '@rate_limit'],
  crypto:    ['@crypto.md5', '@crypto.sha1', '@crypto.random', '@crypto.weak_random', '@jwt.signing'],
  import:    ['@import.crypto', '@import.unsafe'],
};

// ── DSL Keywords ───────────────────────────────────────────────────────────────

const DSL_KEYWORDS = [
  'rule', 'version', 'severity', 'confidence', 'languages', 'cwe', 'owasp',
  'query', 'find', 'flow', 'call', 'usage', 'ref', 'from', 'to', 'where',
  'within', 'not', 'uses', 'any', 'all', 'and', 'or', 'passes_through',
  'calls', 'matches', 'in', 'language', 'file', 'exclude', 'report',
  'message', 'fix', 'evidence', 'tests', 'fixture', 'expect', 'engines',
  'literal', 'function', 'of', 'paths', 'pattern', 'import', 'class',
  'annotation', 'variable', 'assignment', 'extends', 'implements',
];

const VALID_SEVERITIES = ['critical', 'high', 'medium', 'low', 'info'];
const VALID_CONFIDENCES = ['high', 'medium', 'low'];

// ── Syntax Highlighting ────────────────────────────────────────────────────────

function highlightCode(text) {
  const kwSet = new Set(DSL_KEYWORDS);
  return text
    .split('\n')
    .map(line => {
      // Comments
      if (line.trimStart().startsWith('#')) {
        return `<span class="cmt">${escapeHtml(line)}</span>`;
      }
      // Tokenize the line
      return line.replace(
        /(@[\w.]+)|("(?:[^"\\]|\\.)*")|(\b\d+\b)|(\b[a-z_]+\b)/g,
        (match, concept, str, num, word) => {
          if (concept) return `<span class="concept">${escapeHtml(concept)}</span>`;
          if (str) return `<span class="str">${escapeHtml(str)}</span>`;
          if (num) return `<span class="num">${escapeHtml(num)}</span>`;
          if (word && kwSet.has(word)) return `<span class="kw">${escapeHtml(word)}</span>`;
          return escapeHtml(match);
        }
      );
    })
    .join('\n');
}

function escapeHtml(s) {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

// ── Rule Parser ────────────────────────────────────────────────────────────────

function parseRule(text) {
  const result = {
    id: '', severity: '', confidence: '', version: '',
    languages: [], cwe: [], owasp: [],
    query: '', message: '', fix: '', excludePaths: [],
  };
  const lines = text.split('\n');

  // Extract rule ID
  const idMatch = text.match(/^rule\s+([\w-]+)\s*:/m);
  if (idMatch) result.id = idMatch[1];

  // Simple key-value extraction
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();

    const kvMatch = line.match(/^(version|severity|confidence):\s*"?([^"\n]+)"?\s*$/);
    if (kvMatch) {
      result[kvMatch[1]] = kvMatch[2].trim();
      continue;
    }

    const langMatch = line.match(/^languages:\s*\[([^\]]*)\]/);
    if (langMatch) {
      result.languages = langMatch[1].split(',').map(s => s.trim().replace(/"/g, '')).filter(Boolean);
      continue;
    }

    const cweMatch = line.match(/^cwe:\s*\[([^\]]*)\]/);
    if (cweMatch) {
      result.cwe = cweMatch[1].split(',').map(s => s.trim().replace(/"/g, '')).filter(Boolean);
      continue;
    }

    const owaspMatch = line.match(/^owasp:\s*\[([^\]]*)\]/);
    if (owaspMatch) {
      result.owasp = owaspMatch[1].split(',').map(s => s.trim().replace(/"/g, '')).filter(Boolean);
      continue;
    }

    // Query block
    if (line.match(/^query:\s*\|/)) {
      const queryLines = [];
      for (let j = i + 1; j < lines.length; j++) {
        if (lines[j].match(/^\S/) && !lines[j].match(/^\s*$/)) break;
        if (lines[j].trim() === '' && j > i + 1) {
          const nextNonEmpty = lines.slice(j + 1).find(l => l.trim() !== '');
          if (nextNonEmpty && !nextNonEmpty.startsWith('    ') && !nextNonEmpty.startsWith('\t')) break;
        }
        queryLines.push(lines[j]);
      }
      result.query = queryLines.map(l => l.replace(/^ {4}/, '')).join('\n').trim();
      continue;
    }

    // Exclude paths
    const pathsMatch = line.match(/^paths:\s*\[([^\]]*)\]/);
    if (pathsMatch) {
      result.excludePaths = pathsMatch[1].split(',').map(s => s.trim().replace(/"/g, '')).filter(Boolean);
      continue;
    }

    // Report fields
    const msgMatch = line.match(/^message:\s*"([^"]*)"/);
    if (msgMatch) { result.message = msgMatch[1]; continue; }

    const fixMatch = line.match(/^fix:\s*"([^"]*)"/);
    if (fixMatch) { result.fix = fixMatch[1]; continue; }
  }

  return result;
}

// ── Validator ──────────────────────────────────────────────────────────────────

function validateRule(parsed, t) {
  const errors = [];
  if (!parsed.id) errors.push(t('ruleEditor.errors.missingId'));
  if (!parsed.severity) errors.push(t('ruleEditor.errors.missingSeverity'));
  else if (!VALID_SEVERITIES.includes(parsed.severity)) errors.push(t('ruleEditor.errors.invalidSeverity'));
  if (parsed.confidence && !VALID_CONFIDENCES.includes(parsed.confidence)) errors.push(t('ruleEditor.errors.invalidConfidence'));
  if (parsed.languages.length === 0) errors.push(t('ruleEditor.errors.missingLanguages'));
  if (!parsed.query) errors.push(t('ruleEditor.errors.missingQuery'));
  if (!parsed.message) errors.push(t('ruleEditor.errors.missingMessage'));
  return errors;
}

// ── Component ──────────────────────────────────────────────────────────────────

export default function RuleEditor() {
  const { t } = useTranslation();
  const [code, setCode] = useState(TEMPLATES.empty);
  const [errors, setErrors] = useState([]);
  const [validated, setValidated] = useState(false);
  const [compileResult, setCompileResult] = useState(null);
  const [compiling, setCompiling] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [conceptOpen, setConceptOpen] = useState(false);
  const [targetPath, setTargetPath] = useState('');
  const [scanRunning, setScanRunning] = useState(false);
  const [scanResults, setScanResults] = useState(null);
  const [scanError, setScanError] = useState('');
  const textareaRef = useRef(null);
  const highlightRef = useRef(null);
  const linesRef = useRef(null);

  const parsed = parseRule(code);
  const lineCount = code.split('\n').length;

  // Sync scroll between textarea, highlight, and line numbers
  const handleScroll = useCallback(() => {
    const ta = textareaRef.current;
    if (highlightRef.current) {
      highlightRef.current.scrollTop = ta.scrollTop;
      highlightRef.current.scrollLeft = ta.scrollLeft;
    }
    if (linesRef.current) {
      linesRef.current.scrollTop = ta.scrollTop;
    }
  }, []);

  // Validate
  const handleValidate = useCallback(() => {
    const errs = validateRule(parsed, t);
    setErrors(errs);
    setValidated(true);
    setCompileResult(null);
  }, [parsed, t]);

  // Compile (mock for now)
  const handleCompile = useCallback(async () => {
    const errs = validateRule(parsed, t);
    setErrors(errs);
    setValidated(true);
    if (errs.length > 0) return;

    setCompiling(true);
    setCompileResult(null);
    try {
      await axios.post(`${config.BASE_URL}/intelligence/compile`, { rule: code });
      setCompileResult({ success: true, message: 'Rule compiled successfully.' });
    } catch (err) {
      // Mock response if endpoint doesn't exist yet
      if (err.response?.status === 404 || !err.response) {
        setCompileResult({ success: true, message: 'Rule compiled successfully (mock).' });
      } else {
        setCompileResult({ success: false, message: err.response?.data?.error || 'Compilation failed.' });
      }
    } finally {
      setCompiling(false);
    }
  }, [code, parsed, t]);

  // Copy to clipboard
  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(code).then(() => {
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    });
  }, [code]);

  // Download as .rule file
  const handleDownload = useCallback(() => {
    const filename = parsed.id ? `${parsed.id}.rule` : 'rule.rule';
    const blob = new Blob([code], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }, [code, parsed.id]);

  // Run scan
  const runScan = useCallback(async () => {
    setScanRunning(true);
    setScanError('');
    setScanResults(null);
    try {
      const response = await authFetch(`${config.BASE_URL}/intelligence/editor/run`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ruleText: code, targetPath }),
      });
      const data = await response.json();
      if (!response.ok) {
        setScanError(data.error || `HTTP ${response.status}`);
        return;
      }
      if (!data.success) {
        setScanError(data.error || 'Scan failed');
        return;
      }
      setScanResults(data);
    } catch (err) {
      setScanError(err.message);
    } finally {
      setScanRunning(false);
    }
  }, [code, targetPath]);

  // Load template
  const handleTemplate = useCallback((e) => {
    const key = e.target.value;
    if (key && TEMPLATES[key]) {
      setCode(TEMPLATES[key]);
      setErrors([]);
      setValidated(false);
      setCompileResult(null);
    }
  }, []);

  // Insert concept at cursor
  const insertConcept = useCallback((concept) => {
    const ta = textareaRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const newCode = code.substring(0, start) + concept + code.substring(end);
    setCode(newCode);
    // Restore cursor after the inserted text
    requestAnimationFrame(() => {
      ta.focus();
      ta.selectionStart = ta.selectionEnd = start + concept.length;
    });
  }, [code]);

  // Handle tab key in textarea
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Tab') {
      e.preventDefault();
      const ta = e.target;
      const start = ta.selectionStart;
      const end = ta.selectionEnd;
      const newCode = code.substring(0, start) + '  ' + code.substring(end);
      setCode(newCode);
      requestAnimationFrame(() => {
        ta.selectionStart = ta.selectionEnd = start + 2;
      });
    }
  }, [code]);

  // Severity badge color
  const severityClass = parsed.severity ? `badge-${parsed.severity}` : '';

  return (
    <div className="rule-editor-page">
      {/* ── Top Bar ──────────────────────────────────────────────────────── */}
      <div className="rule-editor-topbar">
        <h5 className="mb-0">{t('ruleEditor.title')}</h5>
        <span className="text-muted small d-none d-md-inline">—</span>
        <span className="text-muted small d-none d-md-inline">{t('ruleEditor.subtitle')}</span>
        <div className="d-flex align-items-center gap-2 ms-auto flex-wrap">
          {parsed.id && <span className="rule-name">{parsed.id}</span>}
          {parsed.severity && (
            <span className={`badge badge-severity ${severityClass}`}>
              {parsed.severity}
            </span>
          )}
          <button className="btn btn-sm btn-outline-primary" onClick={handleValidate}>
            {t('ruleEditor.validate')}
          </button>
          <button className="btn btn-sm btn-outline-success" onClick={handleCompile} disabled={compiling}>
            {compiling ? '…' : t('ruleEditor.compile')}
          </button>
          <button className="btn btn-sm btn-outline-secondary" onClick={handleCopy}>
            {copySuccess ? t('ruleEditor.copySuccess') : t('ruleEditor.copy')}
          </button>
          <button className="btn btn-sm btn-outline-secondary" onClick={handleDownload}>
            {t('ruleEditor.download')}
          </button>
        </div>
      </div>

      {/* ── Split Layout ─────────────────────────────────────────────────── */}
      <div className="rule-editor-split">
        {/* Left — Editor */}
        <div className="rule-editor-left">
          <div className="card mb-0 flex-grow-1">
            <div className="card-header py-2 d-flex align-items-center justify-content-between">
              <span className="fw-semibold small">{t('ruleEditor.editorLabel')}</span>
            </div>
            <div className={`code-editor-wrapper${errors.length > 0 && validated ? ' has-error' : ''}`}>
              <div className="code-editor-container">
                <div className="code-editor-lines" ref={linesRef}>
                  {Array.from({ length: lineCount }, (_, i) => (
                    <div key={i}>{i + 1}</div>
                  ))}
                </div>
                <pre
                  className="code-editor-highlight"
                  ref={highlightRef}
                  aria-hidden="true"
                  dangerouslySetInnerHTML={{ __html: highlightCode(code) + '\n' }}
                />
                <textarea
                  ref={textareaRef}
                  className="code-editor-textarea"
                  value={code}
                  onChange={(e) => { setCode(e.target.value); setValidated(false); setCompileResult(null); }}
                  onScroll={handleScroll}
                  onKeyDown={handleKeyDown}
                  spellCheck={false}
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                />
              </div>
            </div>
          </div>

          {/* ── Template Selector ────────────────────────────────────────── */}
          <div className="rule-template-section">
            <label className="form-label small fw-semibold">{t('ruleEditor.templateLabel')}</label>
            <select className="form-select form-select-sm" onChange={handleTemplate} defaultValue="">
              <option value="" disabled>— {t('ruleEditor.templateLabel')} —</option>
              <option value="sqlInjection">{t('ruleEditor.templates.sqlInjection')}</option>
              <option value="hardcodedSecrets">{t('ruleEditor.templates.hardcodedSecrets')}</option>
              <option value="weakCrypto">{t('ruleEditor.templates.weakCrypto')}</option>
              <option value="emptyFunction">{t('ruleEditor.templates.emptyFunction')}</option>
              <option value="todoComment">{t('ruleEditor.templates.todoComment')}</option>
              <option value="emptyTryCatch">{t('ruleEditor.templates.emptyTryCatch')}</option>
              <option value="debugLogging">{t('ruleEditor.templates.debugLogging')}</option>
              <optgroup label="── Layer 1 Primitives ──">
                <option value="weakCryptoImport">{t('ruleEditor.templates.weakCryptoImport')}</option>
                <option value="deprecatedAnnotation">{t('ruleEditor.templates.deprecatedAnnotation')}</option>
                <option value="passwordVariable">{t('ruleEditor.templates.passwordVariable')}</option>
                <option value="unsafeClassInheritance">{t('ruleEditor.templates.unsafeClassInheritance')}</option>
              </optgroup>
              <option value="empty">{t('ruleEditor.templates.empty')}</option>
            </select>
          </div>
        </div>

        {/* Right — Preview + Concepts */}
        <div className="rule-editor-right">
          {/* Preview Card */}
          <div className="card rule-preview-card">
            <div className="card-header py-2">
              <span className="fw-semibold small">{t('ruleEditor.previewLabel')}</span>
            </div>
            <div className="card-body py-2">
              <PreviewRow label={t('ruleEditor.preview.ruleId')} value={parsed.id || '—'} />
              <PreviewRow label={t('ruleEditor.preview.severity')} value={
                parsed.severity
                  ? <span className={`badge badge-severity ${severityClass}`}>{parsed.severity}</span>
                  : '—'
              } />
              <PreviewRow label={t('ruleEditor.preview.confidence')} value={parsed.confidence || '—'} />
              <PreviewRow label={t('ruleEditor.preview.languages')} value={
                parsed.languages.length > 0
                  ? parsed.languages.map(l => <span key={l} className="badge bg-secondary me-1">{l}</span>)
                  : '—'
              } />
              <PreviewRow label={t('ruleEditor.preview.cwe')} value={
                parsed.cwe.length > 0
                  ? parsed.cwe.map(c => <span key={c} className="badge bg-warning text-dark me-1">{c}</span>)
                  : '—'
              } />
              <PreviewRow label={t('ruleEditor.preview.owasp')} value={
                parsed.owasp.length > 0
                  ? parsed.owasp.map(o => <span key={o} className="badge bg-info text-dark me-1">{o}</span>)
                  : '—'
              } />
              {parsed.excludePaths.length > 0 && (
                <PreviewRow label={t('ruleEditor.preview.excludePaths')} value={
                  parsed.excludePaths.map(p => <code key={p} className="me-1">{p}</code>)
                } />
              )}
              <PreviewRow label={t('ruleEditor.preview.message')} value={parsed.message || '—'} />
              <PreviewRow label={t('ruleEditor.preview.fix')} value={parsed.fix || '—'} />

              {/* Query block */}
              {parsed.query && (
                <div className="mt-2">
                  <div className="preview-label mb-1">{t('ruleEditor.preview.query')}</div>
                  <div className="rule-query-block"
                    dangerouslySetInnerHTML={{ __html: highlightCode(parsed.query) }}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Validation Errors / Success */}
          {validated && (
            <div className={`card border-${errors.length > 0 ? 'danger' : 'success'}`}>
              <div className="card-body py-2">
                {errors.length === 0 ? (
                  <div className="text-success small fw-semibold">{t('ruleEditor.validationPassed')}</div>
                ) : (
                  <>
                    <div className="text-danger small fw-semibold mb-1">{t('ruleEditor.validationFailed')}</div>
                    <ul className="list-group list-group-flush validation-errors">
                      {errors.map((err, i) => (
                        <li key={i} className="list-group-item list-group-item-danger py-1 small">{err}</li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Compile Result */}
          {compileResult && (
            <div className={`card border-${compileResult.success ? 'success' : 'danger'}`}>
              <div className="card-body py-2">
                <div className={`small fw-semibold text-${compileResult.success ? 'success' : 'danger'}`}>
                  {compileResult.message}
                </div>
              </div>
            </div>
          )}

          {/* Concept Browser */}
          <div className="card concept-browser-card">
            <div className="card-header py-2 d-flex align-items-center justify-content-between">
              <span className="fw-semibold small">{t('ruleEditor.conceptBrowser')}</span>
              <button
                className="btn btn-sm btn-link p-0 text-decoration-none"
                onClick={() => setConceptOpen(o => !o)}
              >
                {conceptOpen ? '▾' : '▸'}
              </button>
            </div>
            {conceptOpen && (
              <div className="card-body py-2">
                {Object.entries(CONCEPTS).map(([cat, items]) => (
                  <div key={cat} className="concept-category">
                    <div className="concept-category-label">
                      {t(`ruleEditor.conceptCategories.${cat}`)}
                    </div>
                    <div>
                      {items.map(c => (
                        <span
                          key={c}
                          className="concept-tag"
                          onClick={() => insertConcept(c)}
                          title={`Insert ${c}`}
                        >
                          {c}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Run Scan ───────────────────────────────────────────────────── */}
      <div className="rule-editor-run mt-3">
        <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
          <input
            type="text"
            className="form-control"
            placeholder={t('ruleEditor.targetPathPlaceholder')}
            value={targetPath}
            onChange={(e) => setTargetPath(e.target.value)}
            style={{ maxWidth: 400 }}
          />
          <button
            className="btn btn-primary d-flex align-items-center gap-1"
            onClick={runScan}
            disabled={scanRunning || !code.trim() || !targetPath.trim()}
          >
            {scanRunning ? (
              <>
                <span className="spinner-border spinner-border-sm" role="status" />
                {t('ruleEditor.running')}
              </>
            ) : (
              <>{t('ruleEditor.run')} ▶</>
            )}
          </button>
          <small className="text-muted">{t('ruleEditor.targetPathHint')}</small>
        </div>

        {scanError && (
          <div className="alert alert-danger py-2">{t('ruleEditor.scanError')}: {scanError}</div>
        )}

        {scanResults && (
          <div className="scan-results">
            <div className="d-flex align-items-center gap-2 mb-2 flex-wrap">
              <span className="badge bg-secondary">{t('ruleEditor.scanSummary', { total: scanResults.summary.total })}</span>
              {scanResults.summary.critical > 0 && <span className="severity-badge badge bg-danger">Critical: {scanResults.summary.critical}</span>}
              {scanResults.summary.high > 0 && <span className="severity-badge badge bg-warning text-dark">High: {scanResults.summary.high}</span>}
              {scanResults.summary.medium > 0 && <span className="severity-badge badge" style={{backgroundColor:'#ffc107',color:'#000'}}>Medium: {scanResults.summary.medium}</span>}
              {scanResults.summary.low > 0 && <span className="severity-badge badge bg-info">Low: {scanResults.summary.low}</span>}
              {scanResults.summary.info > 0 && <span className="severity-badge badge bg-secondary">Info: {scanResults.summary.info}</span>}
              <small className="text-muted ms-auto">{scanResults.duration}</small>
            </div>

            {scanResults.findings.length === 0 ? (
              <div className="alert alert-success py-2 mb-0">{t('ruleEditor.noFindings')}</div>
            ) : (
              <table className="table table-sm table-striped mb-0">
                <thead>
                  <tr>
                    <th>{t('ruleEditor.findingsTable.severity')}</th>
                    <th>{t('ruleEditor.findingsTable.file')}</th>
                    <th>{t('ruleEditor.findingsTable.line')}</th>
                    <th>{t('ruleEditor.findingsTable.message')}</th>
                  </tr>
                </thead>
                <tbody>
                  {scanResults.findings.map((f, i) => (
                    <tr key={i}>
                      <td><span className={`severity-badge badge badge-${f.severity}`}>{f.severity}</span></td>
                      <td className="file-path">{f.file}</td>
                      <td className="line-number">{f.line}:{f.column}</td>
                      <td>{f.message}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Preview Row Helper ─────────────────────────────────────────────────────────

function PreviewRow({ label, value }) {
  return (
    <div className="preview-row">
      <span className="preview-label">{label}</span>
      <span className="preview-value">{value}</span>
    </div>
  );
}
